/**
 * A wall of recordings: tiles grouped by the month they were performed in, newest first (canon FV6-01,
 * and variant A of `docs/design/nagrania-klientow-2026-09-24/`). Two consumers: the Forma tab's
 * „Nagrania" (one client) and `/nagrania` (every coached client).
 *
 * The caller hands the clips in the order they stand — newest first — and the months follow that
 * order, so a month split across two pages of a paged read is still one month once both are here.
 *
 * How a tile writes its set is the caller's (`entities/lift-history`'s `filmedSetWords`), because
 * only the caller may know the movement's history.
 */

import { dateTimeFormat } from '~/shared/lib/intlCache';
import { capitaliseFirst } from '~/shared/lib/localeCase';
import type { Translate } from '~/shared/lib/localeTranslate';

import {
  clipPersonView,
  formatClock,
  type ClipEntry,
  type ClipMedia,
  type ClipPerson,
  type ClipPersonView,
} from './clipEntry';

/** What a tile needs of a recording: a timeline's `ClipEntry` is one, and so is a collection tile. */
export type TileClip = Pick<ClipEntry, 'id' | 'performedOn' | 'unread' | 'author'> & {
  readonly media: Pick<ClipMedia, 'posterUrl' | 'durationSeconds'>;
};

export interface GalleryClip {
  readonly clip: TileClip;
  readonly exerciseName: string;
  /** What follows the day on the tile's line: „Seria 3 z 4", „90 kg × 5", or „Poza sesją". */
  readonly setWords: readonly string[];
  /** Whose recording it is, on a wall of more than one client; `null` on one client's own. */
  readonly client: ClipPerson | null;
}

export interface ClipTile {
  readonly clipId: number;
  readonly exerciseName: string;
  readonly posterUrl: string | null;
  /** "0:07", or `null` when the wire gave no duration. */
  readonly duration: string | null;
  readonly unread: boolean;
  /** „22 wrz, Seria 3, 90 kg × 5" or „22 wrz, Poza sesją". */
  readonly line: string;
  /** Who filmed it, under the line; `null` where the wall does not say. */
  readonly by: ClipPersonView | null;
  /** The client, over the movement: „Tomasz W."; `null` on one client's own wall. */
  readonly client: ClipPersonView | null;
  /** The tile's accessible name. */
  readonly label: string;
}

export interface ClipMonth {
  readonly key: string;
  /** „Wrzesień 2026". */
  readonly label: string;
  readonly tiles: readonly ClipTile[];
}

export interface ClipMonthsDeps {
  readonly t: Translate;
  readonly locale: string;
  readonly viewerUserId: string | null;
  /** Whether a tile names who filmed it. Forma does; the collection names the client instead. */
  readonly author: boolean;
}

/** „Tomasz W.": the first name and the initial of the last, as a wall of many clients marks a tile. */
function clientMark(person: ClipPerson): ClipPersonView {
  const [first = person.fullName, last] = person.fullName.trim().split(/\s+/);
  const initial = last?.[0];
  return {
    name: initial === undefined ? first : `${first} ${initial}.`,
    initials: person.initials,
    avatarUrl: person.avatarUrl,
    isYou: false,
  };
}

export function clipMonths(clips: readonly GalleryClip[], deps: ClipMonthsDeps): readonly ClipMonth[] {
  const { t, locale } = deps;
  const dayMonth = dateTimeFormat(locale, { day: 'numeric', month: 'short' });
  const monthYear = dateTimeFormat(locale, { month: 'long', year: 'numeric' });

  const months: { key: string; label: string; tiles: ClipTile[] }[] = [];
  for (const { clip, exerciseName, setWords, client } of clips) {
    const day = new Date(clip.performedOn);
    const key = `${String(day.getFullYear())}-${String(day.getMonth())}`;
    let month = months.at(-1);
    if (month?.key !== key) {
      month = { key, label: capitaliseFirst(monthYear.format(day), locale), tiles: [] };
      months.push(month);
    }
    const line = [dayMonth.format(day), ...setWords].join(', ');
    const by = clipPersonView(clip.author, deps.viewerUserId, t('web:forma.you'));
    month.tiles.push({
      clipId: clip.id,
      exerciseName,
      posterUrl: clip.media.posterUrl,
      duration:
        clip.media.durationSeconds === null ? null : formatClock(clip.media.durationSeconds, false, locale),
      unread: clip.unread,
      line,
      by: deps.author ? by : null,
      client: client === null ? null : clientMark(client),
      label: t(clip.unread ? 'web:forma.gallery.openNew' : 'web:forma.gallery.open', {
        name: exerciseName,
        line,
        by: client === null ? by.name : client.fullName,
      }),
    });
  }
  return months;
}
